// Date revival utility for handling JSON date conversion
export class DateReviver {
  private static readonly DATE_PATTERNS = [
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO format
    /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
    /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
    /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY
  ];

  private static readonly KNOWN_DATE_FIELDS = [
    'dateOfBirth', 'validFrom', 'validTo', 'createdAt', 'updatedAt',
    'bookingDateTimeUtc', 'bookingEndTimeUtc', 'startTime', 'endTime',
    'createdDate', 'modifiedDate', 'expiryDate', 'startDate', 'endDate',
    'paymentDate', 'timestamp', 'date', 'checkInUtc'
  ];

  /**
   * Revive dates in a JSON object
   * @param obj - The object to process
   * @param customDateFields - Additional date field names to check
   * @returns The object with dates converted to Date objects
   */
  static revive(obj: any, customDateFields: string[] = []): any {
    const allDateFields = [...this.KNOWN_DATE_FIELDS, ...customDateFields];
    
    return this.processValue(obj, allDateFields);
  }

  /**
   * Parse JSON string and revive dates
   * @param jsonString - The JSON string to parse
   * @param customDateFields - Additional date field names to check
   * @returns The parsed object with dates converted to Date objects
   */
  static parse(jsonString: string, customDateFields: string[] = []): any {
    if (!jsonString || jsonString.trim() === '') {
      return null;
    }
    try {
      const obj = JSON.parse(jsonString);
      return this.revive(obj, customDateFields);
    } catch (error) {
      console.error('Error parsing JSON:', error, 'JSON string:', jsonString);
      throw error;
    }
  }

  private static processValue(value: any, dateFields: string[]): any {
    if (value === null || value === undefined) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(item => this.processValue(item, dateFields));
    }

    if (typeof value === 'object') {
      const result: any = {};
      for (const [key, val] of Object.entries(value)) {
        if (typeof val === 'string' && this.shouldConvertToDate(key, val, dateFields)) {
          result[key] = this.convertToDate(val);
        } else {
          result[key] = this.processValue(val, dateFields);
        }
      }
      return result;
    }

    return value;
  }

  private static shouldConvertToDate(key: string, value: string, dateFields: string[]): boolean {
    // Check if it's a known date field
    if (dateFields.includes(key)) {
      return true;
    }

    // Check if the string matches date patterns
    return this.DATE_PATTERNS.some(pattern => pattern.test(value));
  }

  private static convertToDate(dateString: string): Date | string {
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } catch (error) {
      console.warn(`Failed to convert string to date: ${dateString}`, error);
    }
    
    // Return original string if conversion fails
    return dateString;
  }

  /**
   * Check if a value is a Date object
   */
  static isDate(value: any): value is Date {
    return value instanceof Date && !isNaN(value.getTime());
  }

  /**
   * Check if a string looks like a date
   */
  static isDateString(str: string): boolean {
    return this.DATE_PATTERNS.some(pattern => pattern.test(str));
  }
} 